/**
 * RECIPE_DB — Phase 1, incrément 10.
 *
 * Port direct des 20 recettes du proto (`docs/legacy-prototype/
 * nestor_prod.html` l. 825-846). Sert de fallback local en mode
 * sans backend (Phase 1) et complétera la base CookAPI réelle
 * en Phase 4 quand l'API sera branchée.
 *
 * **Aucune recette n'a été inventée ou modifiée** — fidélité totale
 * au proto (ingrédients, quantités, étapes, tags, cuisines).
 */

export interface Recipe {
  id: number;
  name: string;
  /** Icône emoji (port du champ `ico` proto). */
  icon: string;
  /** Étiquette catégorie (Facile / Familial / Léger / WE / etc.). */
  tag: string;
  cuisine: string;
  /** Temps en minutes. */
  time: number;
  /** Nombre de portions de base (`bs` du proto). */
  bs: number;
  /** Difficulté (Facile / Moyen / Difficile). */
  diff: 'Facile' | 'Moyen' | 'Difficile';
  ing: { n: string; q: number; u: string }[];
  steps: string[];
}

export const RECIPE_DB: readonly Recipe[] = [
  {
    id: 1,
    name: 'Pâtes Bolognaise',
    icon: '🍝',
    tag: 'Facile',
    cuisine: 'Italien',
    time: 30,
    bs: 4,
    diff: 'Facile',
    ing: [
      { n: 'pâtes', q: 400, u: 'g' },
      { n: 'viande hachée', q: 300, u: 'g' },
      { n: 'tomates pelées', q: 400, u: 'g' },
      { n: 'oignons', q: 2, u: 'pcs' },
      { n: 'ail', q: 2, u: 'gousses' },
      { n: "huile d'olive", q: 2, u: 'cs' },
    ],
    steps: [
      "Faire revenir oignon et ail dans l'huile 3 min.",
      'Ajouter la viande, cuire 5 min.',
      'Verser les tomates, mijoter 15 min.',
      'Cuire les pâtes al dente.',
      'Mélanger et servir.',
    ],
  },
  {
    id: 2,
    name: 'Poulet Rôti aux Légumes',
    icon: '🍗',
    tag: 'Familial',
    cuisine: 'Français',
    time: 60,
    bs: 4,
    diff: 'Moyen',
    ing: [
      { n: 'poulet entier', q: 1500, u: 'g' },
      { n: 'pommes de terre', q: 600, u: 'g' },
      { n: 'carottes', q: 300, u: 'g' },
      { n: 'thym', q: 2, u: 'branches' },
      { n: "huile d'olive", q: 3, u: 'cs' },
      { n: 'ail', q: 4, u: 'gousses' },
    ],
    steps: [
      'Préchauffer le four à 200°C.',
      "Badigeonner le poulet d'huile, sel, thym.",
      'Disposer les légumes autour.',
      'Rôtir 55 min en arrosant régulièrement.',
      'Laisser reposer 5 min avant de servir.',
    ],
  },
  {
    id: 3,
    name: 'Pizza Margherita',
    icon: '🍕',
    tag: 'Enfants',
    cuisine: 'Italien',
    time: 25,
    bs: 4,
    diff: 'Facile',
    ing: [
      { n: 'pâte à pizza', q: 500, u: 'g' },
      { n: 'sauce tomate', q: 200, u: 'ml' },
      { n: 'mozzarella', q: 250, u: 'g' },
      { n: 'basilic frais', q: 10, u: 'feuilles' },
      { n: "huile d'olive", q: 1, u: 'cs' },
    ],
    steps: [
      'Étaler la pâte sur 3mm.',
      'Napper de sauce tomate.',
      'Ajouter la mozzarella en tranches.',
      'Cuire 12 min à 220°C.',
      "Garnir de basilic et d'un filet d'huile.",
    ],
  },
  {
    id: 4,
    name: 'Risotto aux Champignons',
    icon: '🍄',
    tag: 'Végétarien',
    cuisine: 'Italien',
    time: 35,
    bs: 4,
    diff: 'Moyen',
    ing: [
      { n: 'riz arborio', q: 320, u: 'g' },
      { n: 'champignons', q: 300, u: 'g' },
      { n: 'bouillon de légumes', q: 1000, u: 'ml' },
      { n: 'oignon', q: 1, u: 'pcs' },
      { n: 'parmesan', q: 60, u: 'g' },
      { n: 'beurre', q: 30, u: 'g' },
      { n: 'vin blanc', q: 100, u: 'ml' },
    ],
    steps: [
      "Faire revenir l'oignon au beurre.",
      'Ajouter les champignons, cuire 5 min.',
      'Incorporer le riz, nacrer 2 min.',
      'Déglacer au vin blanc.',
      'Ajouter le bouillon louche par louche en remuant.',
      'Mantecatura avec parmesan et beurre.',
    ],
  },
  {
    id: 5,
    name: 'Soupe de Légumes',
    icon: '🥦',
    tag: 'Léger',
    cuisine: 'Français',
    time: 30,
    bs: 4,
    diff: 'Facile',
    ing: [
      { n: 'poireaux', q: 2, u: 'pcs' },
      { n: 'carottes', q: 3, u: 'pcs' },
      { n: 'navets', q: 2, u: 'pcs' },
      { n: 'pommes de terre', q: 2, u: 'pcs' },
      { n: 'bouillon de légumes', q: 1200, u: 'ml' },
      { n: 'crème fraîche', q: 2, u: 'cs' },
    ],
    steps: [
      'Éplucher et couper tous les légumes en cubes.',
      'Porter le bouillon à ébullition.',
      'Ajouter les légumes, cuire 20 min.',
      'Mixer partiellement.',
      'Servir avec une cuillère de crème.',
    ],
  },
  {
    id: 6,
    name: 'Saumon en Papillote',
    icon: '🐟',
    tag: 'Léger',
    cuisine: 'Français',
    time: 25,
    bs: 4,
    diff: 'Facile',
    ing: [
      { n: 'filets de saumon', q: 600, u: 'g' },
      { n: 'citron', q: 1, u: 'pcs' },
      { n: 'aneth', q: 1, u: 'bouquet' },
      { n: 'beurre', q: 20, u: 'g' },
      { n: 'sel et poivre', q: 1, u: 'pcs' },
    ],
    steps: [
      'Préchauffer le four à 180°C.',
      'Placer chaque filet sur du papier alu.',
      'Ajouter beurre, citron, aneth, sel.',
      'Fermer les papillotes hermétiquement.',
      'Cuire 18 min.',
    ],
  },
  {
    id: 7,
    name: 'Curry de Poulet',
    icon: '🍛',
    tag: 'Épicé',
    cuisine: 'Indien',
    time: 40,
    bs: 4,
    diff: 'Moyen',
    ing: [
      { n: 'poulet', q: 700, u: 'g' },
      { n: 'lait de coco', q: 400, u: 'ml' },
      { n: 'tomates concassées', q: 400, u: 'g' },
      { n: 'curry en poudre', q: 2, u: 'cs' },
      { n: 'oignon', q: 1, u: 'pcs' },
      { n: 'gingembre', q: 1, u: 'pcs' },
      { n: 'riz basmati', q: 300, u: 'g' },
    ],
    steps: [
      'Faire revenir oignon, gingembre et curry 2 min.',
      'Ajouter le poulet en morceaux, dorer.',
      'Verser tomates et lait de coco.',
      'Mijoter 25 min.',
      'Servir avec le riz basmati.',
    ],
  },
  {
    id: 8,
    name: 'Quiche Lorraine',
    icon: '🥧',
    tag: '',
    cuisine: 'Français',
    time: 50,
    bs: 6,
    diff: 'Moyen',
    ing: [
      { n: 'pâte brisée', q: 1, u: 'pcs' },
      { n: 'lardons fumés', q: 200, u: 'g' },
      { n: 'crème fraîche', q: 250, u: 'ml' },
      { n: 'œufs', q: 3, u: 'pcs' },
      { n: 'gruyère râpé', q: 80, u: 'g' },
      { n: 'noix de muscade', q: 1, u: 'pincée' },
    ],
    steps: [
      'Foncer le moule de pâte brisée.',
      'Faire revenir les lardons, disposer sur la pâte.',
      'Mélanger œufs, crème, muscade, sel.',
      "Verser l'appareil, parsemer de gruyère.",
      'Cuire 35 min à 180°C.',
    ],
  },
  {
    id: 9,
    name: 'Gratin Dauphinois',
    icon: '🥔',
    tag: '',
    cuisine: 'Français',
    time: 80,
    bs: 6,
    diff: 'Moyen',
    ing: [
      { n: 'pommes de terre', q: 1200, u: 'g' },
      { n: 'crème liquide', q: 500, u: 'ml' },
      { n: 'ail', q: 2, u: 'gousses' },
      { n: 'gruyère râpé', q: 100, u: 'g' },
      { n: 'beurre', q: 20, u: 'g' },
      { n: 'noix de muscade', q: 1, u: 'pincée' },
    ],
    steps: [
      'Éplucher et trancher les pommes de terre finement.',
      "Frotter le plat avec l'ail.",
      'Disposer les tranches en couches, sel, muscade.',
      'Verser la crème, parsemer de gruyère.',
      'Cuire 70 min à 170°C.',
    ],
  },
  {
    id: 10,
    name: 'Tacos Maison',
    icon: '🌮',
    tag: 'Enfants',
    cuisine: 'Mexicain',
    time: 20,
    bs: 4,
    diff: 'Facile',
    ing: [
      { n: 'galettes de blé', q: 8, u: 'pcs' },
      { n: 'bœuf haché', q: 400, u: 'g' },
      { n: 'épices à tacos', q: 2, u: 'cs' },
      { n: 'cheddar râpé', q: 100, u: 'g' },
      { n: 'laitue', q: 100, u: 'g' },
      { n: 'tomates', q: 2, u: 'pcs' },
      { n: 'crème aigre', q: 4, u: 'cs' },
    ],
    steps: [
      'Cuire le bœuf à la poêle avec les épices.',
      'Préparer les garnitures.',
      'Réchauffer les galettes.',
      'Garnir et servir aussitôt.',
    ],
  },
  {
    id: 11,
    name: 'Lasagnes Maison',
    icon: '🍝',
    tag: 'Familial',
    cuisine: 'Italien',
    time: 75,
    bs: 6,
    diff: 'Difficile',
    ing: [
      { n: 'pâtes à lasagne', q: 300, u: 'g' },
      { n: 'viande hachée', q: 600, u: 'g' },
      { n: 'sauce tomate', q: 700, u: 'ml' },
      { n: 'béchamel', q: 500, u: 'ml' },
      { n: 'parmesan', q: 80, u: 'g' },
      { n: 'oignon', q: 1, u: 'pcs' },
    ],
    steps: [
      'Préparer la bolognaise 20 min.',
      'Préparer ou décongeler la béchamel.',
      'Alterner : pâtes, bolognaise, béchamel.',
      'Terminer par béchamel et parmesan.',
      'Cuire 40 min à 180°C.',
    ],
  },
  {
    id: 12,
    name: 'Burger Maison',
    icon: '🍔',
    tag: 'WE',
    cuisine: 'Américain',
    time: 25,
    bs: 4,
    diff: 'Facile',
    ing: [
      { n: 'steaks hachés', q: 600, u: 'g' },
      { n: 'pains à burger', q: 4, u: 'pcs' },
      { n: 'cheddar', q: 4, u: 'tranches' },
      { n: 'laitue', q: 4, u: 'feuilles' },
      { n: 'tomates', q: 2, u: 'pcs' },
      { n: 'oignons rouges', q: 1, u: 'pcs' },
      { n: 'sauce burger', q: 4, u: 'cs' },
    ],
    steps: [
      'Cuire les steaks 3 min de chaque côté.',
      'Toaster les pains.',
      "Assembler dans l'ordre : pain, sauce, laitue, steak, cheddar, tomate, oignon.",
    ],
  },
  {
    id: 13,
    name: 'Ratatouille',
    icon: '🫑',
    tag: 'Végétarien',
    cuisine: 'Français',
    time: 60,
    bs: 4,
    diff: 'Moyen',
    ing: [
      { n: 'aubergines', q: 2, u: 'pcs' },
      { n: 'courgettes', q: 2, u: 'pcs' },
      { n: 'poivrons', q: 2, u: 'pcs' },
      { n: 'tomates', q: 4, u: 'pcs' },
      { n: 'oignon', q: 1, u: 'pcs' },
      { n: 'ail', q: 3, u: 'gousses' },
      { n: 'herbes de Provence', q: 1, u: 'cs' },
      { n: "huile d'olive", q: 4, u: 'cs' },
    ],
    steps: [
      'Couper tous les légumes en rondelles.',
      "Faire revenir l'oignon et l'ail.",
      'Disposer les légumes en couches dans un plat.',
      "Arroser d'huile, herbes, sel, poivre.",
      'Cuire 45 min à 180°C.',
    ],
  },
  {
    id: 14,
    name: 'Crêpes',
    icon: '🥞',
    tag: 'Enfants',
    cuisine: 'Français',
    time: 20,
    bs: 4,
    diff: 'Facile',
    ing: [
      { n: 'farine', q: 250, u: 'g' },
      { n: 'lait', q: 500, u: 'ml' },
      { n: 'œufs', q: 3, u: 'pcs' },
      { n: 'beurre fondu', q: 50, u: 'g' },
      { n: 'sucre', q: 1, u: 'cs' },
      { n: 'sel', q: 1, u: 'pincée' },
    ],
    steps: [
      'Mélanger farine et œufs.',
      'Incorporer le lait progressivement.',
      'Ajouter beurre, sucre, sel.',
      'Laisser reposer 30 min.',
      'Cuire chaque crêpe 1 min de chaque côté.',
    ],
  },
  {
    id: 15,
    name: 'Wok de Légumes',
    icon: '🥢',
    tag: 'Végétarien',
    cuisine: 'Asiatique',
    time: 15,
    bs: 2,
    diff: 'Facile',
    ing: [
      { n: 'légumes variés', q: 600, u: 'g' },
      { n: 'sauce soja', q: 3, u: 'cs' },
      { n: 'huile de sésame', q: 1, u: 'cs' },
      { n: 'gingembre', q: 1, u: 'pcs' },
      { n: 'ail', q: 2, u: 'gousses' },
      { n: 'graines de sésame', q: 1, u: 'cs' },
    ],
    steps: [
      'Chauffer fortement le wok.',
      'Faire revenir ail et gingembre 30 sec.',
      'Ajouter les légumes, cuire 5 min en remuant.',
      'Assaisonner avec sauce soja et sésame.',
      'Servir immédiatement.',
    ],
  },
  {
    id: 16,
    name: 'Tiramisu',
    icon: '🍮',
    tag: 'Dessert',
    cuisine: 'Italien',
    time: 20,
    bs: 6,
    diff: 'Moyen',
    ing: [
      { n: 'mascarpone', q: 500, u: 'g' },
      { n: 'œufs', q: 4, u: 'pcs' },
      { n: 'sucre', q: 80, u: 'g' },
      { n: 'biscuits cuillère', q: 200, u: 'g' },
      { n: 'café fort', q: 200, u: 'ml' },
      { n: 'cacao en poudre', q: 2, u: 'cs' },
    ],
    steps: [
      'Séparer blancs et jaunes.',
      "Fouetter jaunes + sucre jusqu'au blanchiment.",
      'Incorporer mascarpone.',
      'Monter les blancs en neige, plier délicatement.',
      'Tremper les biscuits dans le café, alterner avec la crème.',
      'Réfrigérer 4h minimum, saupoudrer de cacao.',
    ],
  },
  {
    id: 17,
    name: 'Tarte aux Pommes',
    icon: '🍎',
    tag: 'Dessert',
    cuisine: 'Français',
    time: 45,
    bs: 6,
    diff: 'Facile',
    ing: [
      { n: 'pâte feuilletée', q: 1, u: 'pcs' },
      { n: 'pommes', q: 4, u: 'pcs' },
      { n: 'sucre', q: 3, u: 'cs' },
      { n: 'beurre', q: 30, u: 'g' },
      { n: 'cannelle', q: 1, u: 'cc' },
      { n: "gelée d'abricot", q: 2, u: 'cs' },
    ],
    steps: [
      'Étaler la pâte dans le moule.',
      'Éplucher et trancher les pommes finement.',
      'Disposer en rosace sur la pâte.',
      'Saupoudrer de sucre et cannelle, parsemer de beurre.',
      'Cuire 35 min à 180°C.',
      'Napper de gelée réchauffée.',
    ],
  },
  {
    id: 18,
    name: "Soupe à l'Oignon",
    icon: '🧅',
    tag: '',
    cuisine: 'Français',
    time: 50,
    bs: 4,
    diff: 'Moyen',
    ing: [
      { n: 'oignons', q: 1000, u: 'g' },
      { n: 'bouillon de bœuf', q: 1000, u: 'ml' },
      { n: 'vin blanc', q: 100, u: 'ml' },
      { n: 'beurre', q: 40, u: 'g' },
      { n: 'pain', q: 4, u: 'tranches' },
      { n: 'gruyère râpé', q: 100, u: 'g' },
    ],
    steps: [
      'Émincer les oignons et les caraméliser au beurre 30 min.',
      'Déglacer au vin blanc.',
      'Ajouter le bouillon, mijoter 15 min.',
      'Verser dans des bols, déposer le pain toasté.',
      'Couvrir de gruyère et gratiner 5 min au gril.',
    ],
  },
  {
    id: 19,
    name: 'Pad Thaï',
    icon: '🍜',
    tag: 'Épicé',
    cuisine: 'Thaïlandais',
    time: 25,
    bs: 2,
    diff: 'Moyen',
    ing: [
      { n: 'nouilles de riz', q: 200, u: 'g' },
      { n: 'crevettes', q: 200, u: 'g' },
      { n: 'œufs', q: 2, u: 'pcs' },
      { n: 'sauce nam pla', q: 2, u: 'cs' },
      { n: 'sauce soja', q: 1, u: 'cs' },
      { n: 'sucre', q: 1, u: 'cs' },
      { n: 'cacahuètes', q: 50, u: 'g' },
      { n: 'citron vert', q: 1, u: 'pcs' },
    ],
    steps: [
      "Tremper les nouilles 20 min dans l'eau froide.",
      'Faire sauter les crevettes, réserver.',
      'Brouiller les œufs dans le wok.',
      'Ajouter les nouilles égouttées, sauces, sucre.',
      'Incorporer les crevettes.',
      'Servir avec cacahuètes et citron vert.',
    ],
  },
  {
    id: 20,
    name: 'Raclette',
    icon: '🧀',
    tag: 'WE',
    cuisine: 'Français',
    time: 15,
    bs: 6,
    diff: 'Facile',
    ing: [
      { n: 'fromage à raclette', q: 1200, u: 'g' },
      { n: 'pommes de terre', q: 1500, u: 'g' },
      { n: 'charcuterie variée', q: 500, u: 'g' },
      { n: 'cornichons', q: 200, u: 'g' },
      { n: 'oignons au vinaigre', q: 100, u: 'g' },
    ],
    steps: [
      "Cuire les pommes de terre à l'eau 20 min.",
      'Disposer charcuterie et accompagnements.',
      'Faire fondre le fromage dans les coupelles.',
      'Napper les pommes de terre et déguster.',
    ],
  },
];
